import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Flex, Button, Icon, Text } from '@kognitos/lattice';

import {
  useBrowserReplay,
  useRecordingMetadata,
} from '@/modules/browser-automation';
import { resolveArtifactsBaseUrl } from '@/shared/utils/browser-urls';

import type { TBrowserPreviewStatus, TResolvedValue } from '../../types';
import { getBrowserLiveViewUrl, isBrowserInstance } from '../../utils';
import {
  BrowserPreviewStatusBadge,
  BrowserPreviewStatusContext,
} from '../BrowserPreviewStatus';
import { PreviewContent } from './PreviewContent';

/**
 * Props for the BrowserLayout component.
 */
interface IBrowserLayoutProps {
  /** The selected value to preview */
  value: TResolvedValue | null;
  /** Output name for the preview */
  outputName: string;
  /** Callback to close the layover */
  onClose: () => void;
  /** Treat the session as ended for replay (run paused for exception guidance). */
  forceSessionEnded?: boolean;
}

/**
 * Layout for browser instance outputs.
 * Shows a "Browser Operator" header with Globe icon / close button,
 * and the browser preview content below.
 *
 * A "Replay" badge appears inline next to the title once the session's
 * metadata.json reports a finalized recording. We read metadata.json
 * through the shared {@link useRecordingMetadata} React Query cache, so
 * this hook does NOT trigger a duplicate fetch — the inner BrowserRenderer's
 * useReplayVideo poll feeds the same cache and BrowserLayout just
 * subscribes to it.
 *
 * @param props - Component props
 * @returns Browser layout with header and preview content
 */
export function BrowserLayout({
  value,
  outputName,
  onClose,
  forceSessionEnded,
}: IBrowserLayoutProps) {
  const { t } = useTranslation();
  const { t: tBrowser } = useTranslation('browser-automation');
  const [playerStatus, setPlayerStatus] = useState<TBrowserPreviewStatus>(null);

  // Derive the artifacts URL from the browser value, then subscribe to
  // the shared metadata cache. Polling lives in BrowserRenderer's
  // useReplayVideo — this hook is purely a reader.
  const artifactsBase = useMemo(() => {
    if (!value || !isBrowserInstance(value)) return null;
    const liveViewUrl = getBrowserLiveViewUrl(value);
    return liveViewUrl ? resolveArtifactsBaseUrl(liveViewUrl) : null;
  }, [value]);
  const { data: metadata } = useRecordingMetadata(artifactsBase);
  // Run-paused signal comes from context; an explicit prop still overrides it.
  const { forceSessionEnded: contextForceSessionEnded } = useBrowserReplay();
  const paused = forceSessionEnded ?? contextForceSessionEnded;
  // metadata.json now exists mid-run (the recording is fetchable during an
  // exception pause), so a recording filename alone no longer means "replay".
  // Show the Replay badge when the run is paused for guidance OR the recording
  // isn't explicitly in-progress. The `!== false` (rather than `=== true`) is
  // deliberate: backend metadata written before #91 has no `finalized` field,
  // and that only ever existed on already-finalized recordings — so
  // `undefined` must count as finalized. This keeps completed-run badges
  // correct regardless of FE/BE deploy order; only #91's explicit
  // `finalized: false` (mid-run) suppresses the badge.
  const hasReplayRecording =
    Boolean(metadata?.recording?.filename) &&
    (Boolean(paused) || metadata?.finalized !== false);

  return (
    <Flex
      direction="column"
      gap={6}
      align="center"
      width="full"
      height="full"
      className="overflow-hidden"
      onClick={e => e.stopPropagation()}
    >
      {/* Header: Globe icon + title (+ Live/Replay badge) + close button.
          Light text on the always-dark scrim, as `.output-modal-header` does. */}
      <Flex align="center" justify="between" className="shrink-0 w-full">
        <Flex gap={3} align="center" className="flex-1 h-9">
          <Icon
            type="Globe"
            className="size-6 text-background dark:text-foreground"
          />
          <Text className="text-lg font-medium text-background dark:text-foreground">
            {tBrowser('browserAutomation.operator.title')}
          </Text>
          {playerStatus === 'live' && (
            <BrowserPreviewStatusBadge status="live" />
          )}
          {playerStatus !== 'live' && hasReplayRecording && (
            <Badge variant="secondary" data-testid="browser-replay-badge">
              {tBrowser('browserAutomation.video.replayBadge')}
            </Badge>
          )}
        </Flex>

        <Button
          onClick={onClose}
          variant="ghost"
          size="icon"
          className="text-background hover:bg-background/15 dark:text-foreground dark:hover:bg-accent"
          aria-label={t('common.action.close')}
        >
          <Icon type="X" />
        </Button>
      </Flex>

      {/* Browser Preview Content */}
      <div className="flex-1 min-h-0 w-full overflow-auto">
        <BrowserPreviewStatusContext value={setPlayerStatus}>
          <PreviewContent
            value={value}
            onClose={onClose}
            outputName={outputName}
          />
        </BrowserPreviewStatusContext>
      </div>
    </Flex>
  );
}
